if (
  !customElements.get("variant-selects") &&
  !customElements.get("variant-radios")
) {
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("change", this.onVariantChange);
    }

    connectedCallback() {
      this.updateOptions();
      this.updateVariantId();

      if (this.currentVariant) {
        window.SnowcubesGift?.updateGiftNoteCardState?.({ animate: false });
        this.handleGiftWrappedVariantChange();
        return;
      }

      // Fallback: ensure initial variant state is applied once DOM settles.
      setTimeout(() => {
        this.updateOptions();
        this.updateVariantId();
        if (this.currentVariant) {
          window.SnowcubesGift?.updateGiftNoteCardState?.({ animate: false });
          this.handleGiftWrappedVariantChange();
        }
      }, 0);
    }

    onVariantChange() {
      this.updateOptions();
      this.updateVariantId();
      this.toggleAddButton(true);
      this.updatePickupAvailability();
      this.removeErrorMessage();

      //if we couldn't find a currently selected variant for whatever reason
      if (!this.currentVariant) {
        this.toggleAddButton(true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo();
        // this.updateShareUrl();

        // Track variant selection
        if (window.SnowcubesAnalytics && this.currentVariant) {
          window.SnowcubesAnalytics.track(
            window.SnowcubesAnalytics.EVENTS.VARIANT_SELECT,
            {
              variant_id: this.currentVariant.id,
              variant_title: this.currentVariant.title,
              variant_price: this.currentVariant.price / 100,
              options: this.options,
            }
          );
        }
        
        // Operation Santa: Handle Gift Wrapped variant switching
        this.handleGiftWrappedVariantChange();
      }
    }

    findGiftWrappedVariant() {
      const Gift = window.SnowcubesGift;
      const isGiftWrappedTitle = Gift?.isGiftWrappedVariantTitle
        ? Gift.isGiftWrappedVariantTitle.bind(Gift)
        : (title) => /\bgift[-\s]*wrap(ped)?\b/i.test(title || '');

      return this.getVariantData().find((variant) => {
        return variant?.available !== false && isGiftWrappedTitle(variant.title || '');
      }) || null;
    }

    selectGiftWrappedVariant() {
      const giftVariant = this.findGiftWrappedVariant();
      if (!giftVariant) return { ok: false, error: 'gift_variant_not_found' };

      const Gift = window.SnowcubesGift;
      const currentTitle = this.currentVariant?.title || '';
      const isAlreadyGiftWrapped = Gift?.isGiftWrappedVariantTitle
        ? Gift.isGiftWrappedVariantTitle(currentTitle)
        : /\bgift[-\s]*wrap(ped)?\b/i.test(currentTitle);

      if (this.currentVariant?.id === giftVariant.id || isAlreadyGiftWrapped) {
        this.currentVariant = giftVariant;
        this.updateVariantInput();
        this.handleGiftWrappedVariantChange();
        return { ok: true, changed: false, variant: giftVariant };
      }

      this.applyVariantSelection(giftVariant);
      return { ok: true, changed: true, variant: giftVariant };
    }

    applyVariantSelection(variant) {
      if (!variant?.options?.length) return false;

      const selects = Array.from(this.querySelectorAll('select'));
      selects.forEach((select, index) => {
        if (variant.options[index] !== undefined) {
          select.value = variant.options[index];
        }
      });

      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((fieldset, index) => {
        const optionValue = variant.options[index];
        const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
          .find((input) => input.value === optionValue);
        if (radio) radio.checked = true;
      });

      this.updateOptions();
      this.currentVariant = variant;
      this.toggleAddButton(true);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.handleGiftWrappedVariantChange();
      return true;
    }
    
    // Clear gift config when switching away from Gift Wrapped variant
    // Hide dynamic checkout buttons when Gift Wrapped selected but no config
    handleGiftWrappedVariantChange() {
      if (!this.currentVariant) return;

      const variantTitle = this.currentVariant.title || '';
      const Gift = window.SnowcubesGift;
      const isGiftWrapped = Gift?.isGiftWrappedVariantTitle
        ? Gift.isGiftWrappedVariantTitle(variantTitle)
        : false;

      // Resolve only the form owned by this variant picker, not every form in the section.
      const productForm = this.resolveOwningProductForm();
      if (!productForm) {
        Gift?.debug?.('Skipping gift form mutations: owning form could not be resolved', {
          section: this.dataset.section || null,
          productId: this.dataset.productId || null,
        });
      }
      let configInput = productForm?.querySelector('input[name="properties[_GiftCardConfig]"]');

      // Clear stale config if switching away from Gift Wrapped.
      if (!isGiftWrapped && productForm) {
        productForm.querySelector('input[name="properties[_GiftCardConfig]"]')?.remove();
        productForm.querySelector('input[name="properties[_GiftCardTo]"]')?.remove();
        productForm.querySelector('input[name="properties[_GiftCardFrom]"]')?.remove();
        productForm.querySelector('input[name="properties[_GiftCardMessage]"]')?.remove();
        configInput = null;
        Gift?.debug?.('Cleared gift config (switched to non-Gift Wrapped variant)', {
          formId: productForm.id || null,
        });
      }

      let hasConfig = !!configInput?.value;

      if (isGiftWrapped && productForm && !hasConfig) {
        const storedConfig = Gift?.giftConfigPrototype?.load?.() || window.giftConfigPrototype?.load?.();
        if (storedConfig && Gift?.upsertGiftInputs) {
          const result = Gift.upsertGiftInputs(productForm, storedConfig);
          if (result?.ok) {
            configInput = productForm.querySelector('input[name="properties[_GiftCardConfig]"]');
            hasConfig = !!configInput?.value;
            Gift?.debug?.('Rehydrated gift inputs from localStorage on variant switch', {
              formId: productForm.id || null,
            });
          }
        }
      }

      // Keep gift note card state in sync after variant switch.
      Gift?.updateGiftNoteCardState?.({ animate: true });
      
      // Toggle dynamic checkout buttons (Buy Now, Shop Pay, Apple Pay)
      this.togglePaymentButtons(isGiftWrapped, hasConfig, productForm);
    }

    resolveOwningProductForm() {
      const productFormId = this.dataset.section && this.dataset.productId
        ? `Product-Form-${this.dataset.section}${this.dataset.productId}`
        : null;
      if (productFormId) {
        const formById = document.getElementById(productFormId);
        if (formById?.tagName === 'FORM') return formById;
      }

      const associatedControl = this.querySelector('select[form], input[form]');
      const associatedFormId = associatedControl?.getAttribute('form');
      if (associatedFormId) {
        const associatedForm = document.getElementById(associatedFormId);
        if (associatedForm?.tagName === 'FORM') return associatedForm;
      }

      const parentProductForm = this.closest('product-form');
      if (parentProductForm) {
        const formInParent = parentProductForm.querySelector('form');
        if (formInParent?.tagName === 'FORM') return formInParent;
      }

      return null;
    }
    
    togglePaymentButtons(isGiftWrapped, hasConfig, productForm) {
      if (!productForm) return;
      const scope =
        productForm?.closest('product-form') ||
        productForm?.closest('.product-form') ||
        productForm.parentElement;
      const paymentButtons = scope?.querySelectorAll('.shopify-payment-button, [data-shopify-pay]');
      
      if (!paymentButtons?.length) return;
      
      // Hide Buy Now buttons if Gift Wrapped but no config
      const shouldHide = isGiftWrapped && !hasConfig;
      
      paymentButtons.forEach(btn => {
        btn.style.display = shouldHide ? 'none' : '';
      });
      
      if (shouldHide) {
        window.SnowcubesGift?.debug?.('Hiding dynamic checkout buttons (Gift Wrapped needs customization)');
      }
    }

    // Updates currently selected options such as, this.options = [ Red, Small ]
    updateOptions() {
      this.options = Array.from(
        this.querySelectorAll("select"),
        (select) => select.value
      );
    }

    // Using currently selected options, get currently selected variant
    updateVariantId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    //if currently selected variant has featured media, put it first
    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;

      const mediaGallery = document.getElementById(
        `Product-MediaGallery-${this.dataset.section}${this.dataset.productId}`
      );
      
      if (!mediaGallery) return;

      mediaGallery.setActiveMedia(
        `${this.dataset.section}${this.dataset.productId}-${this.currentVariant.featured_media.id}`
      );
    }

    //update the url to include the variant query
    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === "false") return;
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.currentVariant.id}`
      );
    }

    /*updateShareUrl() {
      const shareButton = document.getElementById(
        `Share-${this.dataset.section}${this.dataset.productId}`
      );
      if (!shareButton || !shareButton.updateUrl) return;
      shareButton.updateUrl(
        `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
      );
    }*/

    //Update the current variant id value on all product forms that use it
    updateVariantInput() {
      const productForms = document.querySelectorAll(
        `#Product-Form-${this.dataset.section}${this.dataset.productId}, #Installment-${this.dataset.section}${this.dataset.productId}`
      );

      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    //removes any error messages from the product form customElement
    removeErrorMessage() {
      const section = this.closest("section");
      if (!section) return;

      const formElement = section.querySelector(
        `#Product-Form-${this.dataset.section}${this.dataset.productId}, #Installment-${this.dataset.section}${this.dataset.productId}`
      );
      if (!formElement) return;
      const productForm = formElement.closest("product-form");
      if (productForm) productForm.handleErrorMessage();
    }

    //WOWWW, brilliant. Store new variant page DOM in variable, and populate current page's price with it, and call the toggle add to cart button using new variants avialability variable
  renderProductInfo() {
      const requestedVariantId = this.currentVariant.id;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
        .then((response) => response.text())
        .then((responseText) => {
          if (this.currentVariant.id !== requestedVariantId) return;
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );

          const destination = document.getElementById(
            `Price-${this.dataset.section}${this.dataset.productId}`
          );
            
          const source = html.getElementById(
            `Price-${this.dataset.section}${this.dataset.productId}`
          );

          if (source && destination) {
            destination.innerHTML = source.innerHTML;
          }

          const destinations = document.querySelectorAll(`#Price-${this.dataset.section}${this.dataset.productId}`);
          const sources = html.querySelectorAll(`[id="Price-${this.dataset.section}${this.dataset.productId}"]`);
            
          const priceNodeCount = Math.min(sources.length, destinations.length);
          for (let i = 0; i < priceNodeCount; i++) {
            const source = sources[i];
            const destination = destinations[i];

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
            }
          }

          if (sources.length !== destinations.length) {
            window.SnowcubesGift?.debug?.('Variant price refresh count mismatch', {
              sourceCount: sources.length,
              destinationCount: destinations.length,
            });
          }

          const price = document.getElementById(
            `Price-${this.dataset.section}${this.dataset.productId}`
          );

          if (price) price.classList.remove("visibility-hidden");
          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
          );
        })
        .catch((error) => {
          this.toggleAddButton(false);
          console.error('Failed to fetch product info:', error);
        });
    }

    // update add to cart button's text and disabled state
    toggleAddButton(disable = true, text) {
      const productForm = document.getElementById(
        `Product-Form-${this.dataset.section}${this.dataset.productId}`
      );
      if (!productForm) return;

      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');

      if (!addButton) return;

      if (disable) {
        addButton.setAttribute("disabled", "disabled");
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute("disabled");
        addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    // Change button text to unavailable and hide price
    setUnavailable() {
      const button = document.getElementById(
        `Product-Form-${this.dataset.section}${this.dataset.productId}`
      );
      if (!button) return;
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span');
      const price = document.getElementById(
        `Price-${this.dataset.section}${this.dataset.productId}`
      );

      if (!addButton) return;
      addButtonText.textContent = window.variantStrings.unavailable;

      if (price) price.classList.add("visibility-hidden");
    }

    // Get all product variant data so we can loop through it in other functions
    getVariantData() {
      this.variantData =
        this.variantData ||
        JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector("pickup-availability");
      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.render(this.currentVariant.id);
      } else {
        pickUpAvailability.innerHTML = "";
      }
    }
  }
  customElements.define("variant-selects", VariantSelects);

  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll("fieldset"));
      this.options = fieldsets.map((fieldset) => {
        const checked = Array.from(fieldset.querySelectorAll("input")).find(
          (radio) => radio.checked
        );
        return checked ? checked.value : '';
      });
    }
  }
  customElements.define("variant-radios", VariantRadios);
}
